import { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, TrendingUp, Play, Square, Minus, RotateCcw, Trophy, Target, Users, FileText, Printer, Download, Share2, X, Sparkles, AlertTriangle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import usePermissions from '@/hooks/usePermissions';
import { ReadOnlyBanner, NoAccessScreen } from '@/components/PermissionUI';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAT_TYPES = [
  { key: 'points_2pt', label: '2PT', color: 'bg-blue-500', points: 2 },
  { key: 'points_2pt_miss', label: '2PT MISS', color: 'bg-blue-300', isMiss: true },
  { key: 'points_3pt', label: '3PT', color: 'bg-purple-500', points: 3 },
  { key: 'points_3pt_miss', label: '3PT MISS', color: 'bg-purple-300', isMiss: true },
  { key: 'points_ft', label: 'FT', color: 'bg-green-500', points: 1 },
  { key: 'points_ft_miss', label: 'FT MISS', color: 'bg-green-300', isMiss: true },
  { key: 'rebounds_off', label: 'OFF REB', color: 'bg-orange-500' },
  { key: 'rebounds_def', label: 'DEF REB', color: 'bg-yellow-500' },
  { key: 'assists', label: 'AST', color: 'bg-cyan-500' },
  { key: 'steals', label: 'STL', color: 'bg-emerald-500' },
  { key: 'turnovers', label: 'TO', color: 'bg-red-500' },
  { key: 'fouls', label: 'FOUL', color: 'bg-rose-500' },
  { key: 'technical_fouls', label: 'TECH', color: 'bg-rose-700' }
];

export default function StatsPage({ user, onLogout, permissions }) {
  const { canEdit, canView, isReadOnly } = usePermissions(permissions);
  const [players, setPlayers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [sessionStats, setSessionStats] = useState({});
  const [showNewGameDialog, setShowNewGameDialog] = useState(false);
  const [showBoxScore, setShowBoxScore] = useState(false);
  const [boxScoreSession, setBoxScoreSession] = useState(null);
  const [boxScoreData, setBoxScoreData] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [recentStats, setRecentStats] = useState([]);
  const [currentQuarter, setCurrentQuarter] = useState(1);
  const [oppScores, setOppScores] = useState({ q1: 0, q2: 0, q3: 0, q4: 0, ot: 0 });
  // Order of player cards: most-recently-active player (last stat recorded) floats to the top.
  const [playerOrder, setPlayerOrder] = useState([]);
  const boxScoreRef = useRef(null);
  
  const [newGame, setNewGame] = useState({
    game_name: '',
    game_date: new Date().toISOString().split('T')[0],
    opponent: ''
  });

  useEffect(() => {
    loadPlayers();
    loadSessions();
  }, []);

  useEffect(() => {
    if (activeSession) {
      loadSessionStats(activeSession.id);
    }
  }, [activeSession]);

  // Keep playerOrder in sync with the roster: preserve current order, append any newly-added players.
  useEffect(() => {
    setPlayerOrder(prev => {
      const existing = prev.filter(id => players.some(p => p.id === id));
      const newIds = players.filter(p => !existing.includes(p.id)).map(p => p.id);
      return [...existing, ...newIds];
    });
  }, [players]);

  const orderedPlayers = playerOrder.map(id => players.find(p => p.id === id)).filter(Boolean);

  const loadPlayers = async () => {
    try {
      const response = await axios.get(`${API}/players?team_id=${user.team_id}`);
      setPlayers(response.data);
    } catch (error) {
      console.error('Failed to load players');
    }
  };

  const loadSessions = async () => {
    try {
      const response = await axios.get(`${API}/game-day-stats/sessions?team_id=${user.team_id}`);
      setSessions(response.data);
      const active = response.data.find(s => s.status === 'active');
      if (active) {
        setActiveSession(active);
      }
    } catch (error) {
      console.error('Failed to load sessions');
    }
  };

  const loadSessionStats = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/game-day-stats/summary/${sessionId}`);
      setSessionStats(response.data);
      if (response.data.opponent_quarter_scores) {
        setOppScores(response.data.opponent_quarter_scores);
      }
      return response.data;
    } catch (error) {
      console.error('Failed to load session stats');
      return null;
    }
  };

  const updateOppScore = (quarter, value) => {
    const v = Math.max(0, parseInt(value) || 0);
    const next = { ...oppScores, [quarter]: v };
    setOppScores(next);
    if (activeSession) {
      axios.put(`${API}/game-day-stats/session/${activeSession.id}/opponent-score`, next).catch(() => {});
    }
  };

  const loadBoxScoreData = async (session) => {
    try {
      const response = await axios.get(`${API}/game-day-stats/summary/${session.id}`);
      setBoxScoreData(response.data);
      setBoxScoreSession(session);
      setShowBoxScore(true);
    } catch (error) {
      toast.error('Failed to load box score');
    }
  };

  const startNewGame = async () => {
    if (!newGame.game_name) {
      toast.error('Please enter a game name');
      return;
    }
    try {
      const response = await axios.post(`${API}/game-day-stats/session`, {
        team_id: user.team_id,
        ...newGame
      });
      toast.success('Game session started!');
      setActiveSession(response.data);
      setShowNewGameDialog(false);
      loadSessions();
    } catch (error) {
      toast.error('Failed to start game session');
    }
  };

  const [endedSession, setEndedSession] = useState(null);  // { id, recap_status }
  const [showRecapToast, setShowRecapToast] = useState(false);

  const endGame = async () => {
    if (!activeSession) return;
    try {
      const res = await axios.post(`${API}/game-day-stats/session/${activeSession.id}/end`);
      toast.success('Game session ended');
      const endedId = activeSession.id;
      setEndedSession({ id: endedId, recap_status: res.data.recap_status || 'generating' });
      setShowRecapToast(true);
      setActiveSession(null);
      loadSessions();
      // Poll session recap_status every 5s until ready/failed (max 3 min).
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts += 1;
        try {
          const s = await axios.get(`${API}/game-day-stats/session/${endedId}`);
          const status = s.data?.recap_status;
          if (status && status !== 'generating') {
            clearInterval(poll);
            setEndedSession({ id: endedId, recap_status: status });
            if (status === 'ready') {
              toast.success('Recap draft ready — check the Social Media page!');
            } else if (status === 'failed') {
              toast.warning('Recap draft failed to generate. You can create one manually from Reports.');
            }
          }
        } catch (e) { /* ignore */ }
        if (attempts >= 36) {
          clearInterval(poll);
        }
      }, 5000);
    } catch (error) {
      toast.error('Failed to end game session');
    }
  };

  const takeTimeout = async (timeoutType) => {
    if (!activeSession) return;
    try {
      const res = await axios.put(`${API}/game-day-stats/session/${activeSession.id}/timeout`, { timeout_type: timeoutType });
      setSessionStats(prev => ({ ...prev, timeouts_used: res.data.timeouts_used }));
      toast.success(`${timeoutType === 'full' ? 'Full Timeout' : '30sec Timeout'} used`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record timeout');
    }
  };

  const undoTimeout = async (timeoutType) => {
    if (!activeSession) return;
    try {
      const res = await axios.put(`${API}/game-day-stats/session/${activeSession.id}/timeout/undo`, { timeout_type: timeoutType });
      setSessionStats(prev => ({ ...prev, timeouts_used: res.data.timeouts_used }));
    } catch (error) {
      toast.error('Failed to undo timeout');
    }
  };

  const recordStat = async (playerId, playerName, statType) => {
    if (!activeSession) {
      toast.error('No active game session');
      return;
    }
    
    const formData = new FormData();
    formData.append('session_id', activeSession.id);
    formData.append('player_id', playerId);
    formData.append('player_name', playerName);
    formData.append('stat_type', statType);
    formData.append('value', '1');
    formData.append('quarter', currentQuarter.toString());

    try {
      const response = await axios.post(`${API}/game-day-stats/record`, formData);
      setRecentStats(prev => [response.data, ...prev.slice(0, 9)]);
      setPlayerOrder(prev => [playerId, ...prev.filter(id => id !== playerId)]);
      const updated = await loadSessionStats(activeSession.id);
      
      const statInfo = STAT_TYPES.find(s => s.key === statType);
      toast.success(`Q${currentQuarter} - ${playerName}: +${statInfo?.label || statType}`);

      if (statType === 'fouls' && updated) {
        const playerFouls = updated.player_stats?.find(p => p.player_id === playerId)?.fouls || 0;
        if (playerFouls === 4) {
          toast.error(`${playerName} is in foul trouble (4 fouls)`, { duration: 6000 });
        } else if (currentQuarter === 1 && playerFouls === 3) {
          toast.error(`${playerName}: 3rd foul in Q1 - Need Sub`, { duration: 6000 });
        } else if (currentQuarter === 1 && playerFouls === 2) {
          toast.warning(`${playerName}: 2nd foul in Q1 - Consider Sub`, { duration: 6000 });
        }
      }
    } catch (error) {
      toast.error('Failed to record stat');
    }
  };

  const undoStat = async (statId) => {
    try {
      await axios.delete(`${API}/game-day-stats/undo/${statId}`);
      setRecentStats(prev => prev.filter(s => s.id !== statId));
      if (activeSession) {
        loadSessionStats(activeSession.id);
      }
      toast.success('Stat removed');
    } catch (error) {
      toast.error('Failed to undo stat');
    }
  };

  const getPlayerStats = (playerId) => {
    if (!sessionStats.player_stats) return null;
    return sessionStats.player_stats.find(p => p.player_id === playerId);
  };

  const getFoulTroubleBadge = (playerId) => {
    const stats = getPlayerStats(playerId);
    const fouls = stats?.fouls || 0;
    if (fouls >= 4) return { label: 'FOUL TROUBLE', className: 'bg-rose-700 text-white' };
    if (currentQuarter === 1 && fouls === 3) return { label: 'NEED SUB', className: 'bg-red-600 text-white' };
    if (currentQuarter === 1 && fouls === 2) return { label: 'CONSIDER SUB', className: 'bg-amber-500 text-white' };
    return null;
  };

  const printBoxScore = () => {
    const printContent = boxScoreRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Box Score - ${boxScoreSession?.game_name || 'Game'}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; }
            .box-score-header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #1e3a8a; padding-bottom: 15px; }
            .box-score-header h1 { font-size: 28px; color: #1e3a8a; margin-bottom: 5px; }
            .box-score-header p { color: #666; font-size: 14px; }
            .team-score { font-size: 48px; font-weight: bold; color: #1e3a8a; margin: 10px 0; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: center; font-size: 12px; }
            th { background-color: #1e3a8a; color: white; font-weight: bold; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            tr:hover { background-color: #f1f1f1; }
            .player-name { text-align: left; font-weight: 500; }
            .totals-row { font-weight: bold; background-color: #e5e7eb !important; }
            .footer { text-align: center; margin-top: 20px; font-size: 11px; color: #888; border-top: 1px solid #ddd; padding-top: 10px; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
          <div class="footer">Generated by PAC (Personal AI Coach) • ${new Date().toLocaleString()}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const downloadBoxScore = () => {
    if (!boxScoreData || !boxScoreSession) return;

    // Helper function for percentage
    const calcPct = (made, att) => att > 0 ? ((made / att) * 100).toFixed(1) : '0.0';

    // Create CSV content
    let csv = `BOX SCORE - ${boxScoreSession.game_name}\n`;
    csv += `Date: ${boxScoreSession.game_date}\n`;
    if (boxScoreSession.opponent) csv += `Opponent: ${boxScoreSession.opponent}\n`;
    csv += `\n`;
    csv += `TEAM TOTAL: ${boxScoreData.team_totals?.total_points || 0} POINTS\n`;
    csv += `\n`;
    
    // Quarter breakdown if available
    if (boxScoreData.quarter_totals) {
      csv += `SCORING BY QUARTER:\n`;
      csv += `Team,Q1,Q2,Q3,Q4,OT,FINAL\n`;
      const qt = boxScoreData.quarter_totals;
      const oq = boxScoreData.opponent_quarter_scores || {};
      csv += `US,${qt.q1 || 0},${qt.q2 || 0},${qt.q3 || 0},${qt.q4 || 0},${qt.ot || 0},${boxScoreData.final_score?.us ?? boxScoreData.team_totals?.total_points ?? 0}\n`;
      csv += `${boxScoreSession.opponent || 'OPPONENT'},${oq.q1 || 0},${oq.q2 || 0},${oq.q3 || 0},${oq.q4 || 0},${oq.ot || 0},${boxScoreData.final_score?.opponent ?? 0}\n`;
      if (boxScoreData.final_score) {
        csv += `RESULT,${boxScoreData.final_score.result === 'W' ? 'WIN' : boxScoreData.final_score.result === 'L' ? 'LOSS' : 'TIE'}\n`;
      }
      csv += `\n`;
    }
    
    csv += `Player,PTS,2PT,2PA,2P%,3PT,3PA,3P%,FT,FTA,FT%,OREB,DREB,REB,AST,STL,TO,PF\n`;
    
    boxScoreData.player_stats?.forEach(player => {
      const fg2Att = (player.points_2pt || 0) + (player.points_2pt_miss || 0);
      const fg3Att = (player.points_3pt || 0) + (player.points_3pt_miss || 0);
      const ftAtt = (player.points_ft || 0) + (player.points_ft_miss || 0);
      const fg2Pct = calcPct(player.points_2pt || 0, fg2Att);
      const fg3Pct = calcPct(player.points_3pt || 0, fg3Att);
      const ftPct = calcPct(player.points_ft || 0, ftAtt);
      csv += `${player.player_name},${player.total_points},${player.points_2pt},${fg2Att},${fg2Pct}%,${player.points_3pt},${fg3Att},${fg3Pct}%,${player.points_ft},${ftAtt},${ftPct}%,${player.rebounds_off},${player.rebounds_def},${player.total_rebounds},${player.assists},${player.steals},${player.turnovers},${player.fouls}\n`;
    });

    // Add totals row
    const totals = boxScoreData.team_totals || {};
    csv += `TEAM TOTALS,${totals.total_points || 0},-,-,-,-,-,-,-,-,-,${totals.total_rebounds || 0},${totals.total_assists || 0},${totals.total_steals || 0},${totals.total_turnovers || 0},${totals.total_fouls || 0}\n`;

    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `box-score-${boxScoreSession.game_date}-${boxScoreSession.game_name.replace(/\s+/g, '-')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Box score downloaded!');
  };

  const shareBoxScore = async () => {
    if (!boxScoreData || !boxScoreSession) return;

    const text = generateShareText();
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Box Score - ${boxScoreSession.game_name}`,
          text: text
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(text);
        }
      }
    } else {
      copyToClipboard(text);
    }
  };

  const generateShareText = () => {
    if (!boxScoreData || !boxScoreSession) return '';

    let text = `🏀 BOX SCORE\n`;
    text += `${boxScoreSession.game_name}\n`;
    text += `📅 ${boxScoreSession.game_date}\n`;
    if (boxScoreSession.opponent) text += `🆚 ${boxScoreSession.opponent}\n`;
    text += `\n`;
    text += `FINAL: ${boxScoreData.team_totals?.total_points || 0} PTS\n`;
    text += `━━━━━━━━━━━━━━━\n`;
    
    // Top performers
    const topScorer = boxScoreData.player_stats?.reduce((max, p) => 
      (p.total_points > (max?.total_points || 0)) ? p : max, null);
    
    if (topScorer && topScorer.total_points > 0) {
      text += `⭐ ${topScorer.player_name}: ${topScorer.total_points} PTS\n`;
    }

    text += `\n📊 Team Stats:\n`;
    text += `REB: ${boxScoreData.team_totals?.total_rebounds || 0}\n`;
    text += `AST: ${boxScoreData.team_totals?.total_assists || 0}\n`;
    text += `STL: ${boxScoreData.team_totals?.total_steals || 0}\n`;
    text += `\n#PAC #Basketball`;
    
    return text;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Box score copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const StatCounter = ({ player }) => {
    const stats = getPlayerStats(player.id);
    const playerName = `${player.first_name || ''} ${player.last_name || ''}`.trim() || `#${player.jersey_number}`;
    const foulBadge = getFoulTroubleBadge(player.id);

    // Calculate shooting percentages
    const fg2Made = stats?.points_2pt || 0;
    const fg2Miss = stats?.points_2pt_miss || 0;
    const fg2Att = fg2Made + fg2Miss;
    
    const fg3Made = stats?.points_3pt || 0;
    const fg3Miss = stats?.points_3pt_miss || 0;
    const fg3Att = fg3Made + fg3Miss;
    
    const ftMade = stats?.points_ft || 0;
    const ftMiss = stats?.points_ft_miss || 0;
    const ftAtt = ftMade + ftMiss;

    return (
      <Card className="border-2" data-testid={`player-stat-card-${player.id}`}>
        <CardHeader className="py-3 px-4 bg-primary/5 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold">
                {player.jersey_number || '?'}
              </div>
              <div>
                <h3 className="font-semibold text-sm">{playerName}</h3>
                <p className="text-xs text-muted-foreground">{player.position || 'Player'}</p>
                {foulBadge && (
                  <Badge className={`mt-1 text-[10px] px-1.5 py-0 ${foulBadge.className}`} data-testid={`foul-trouble-badge-${player.id}`}>
                    {foulBadge.label}
                  </Badge>
                )}
              </div>
            </div>
            <Badge variant="secondary" className="text-lg font-bold">
              {stats?.total_points || 0} PTS
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-3">
          {/* Points Section - 2PT */}
          <div className="mb-3">
            <p className="text-xs font-semibold text-muted-foreground mb-2">2-POINTERS ({fg2Made}/{fg2Att})</p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => recordStat(player.id, playerName, 'points_2pt')}
                className="bg-blue-500 hover:bg-blue-600 h-12 flex flex-col"
                data-testid={`stat-btn-${player.id}-points_2pt`}
              >
                <span className="text-lg font-bold">2PT ✓</span>
                <span className="text-xs opacity-75">{fg2Made} made</span>
              </Button>
              <Button
                onClick={() => recordStat(player.id, playerName, 'points_2pt_miss')}
                className="bg-blue-300 hover:bg-blue-400 h-12 flex flex-col text-blue-900"
                data-testid={`stat-btn-${player.id}-points_2pt_miss`}
              >
                <span className="text-lg font-bold">2PT ✗</span>
                <span className="text-xs opacity-75">{fg2Miss} miss</span>
              </Button>
            </div>
          </div>

          {/* Points Section - 3PT */}
          <div className="mb-3">
            <p className="text-xs font-semibold text-muted-foreground mb-2">3-POINTERS ({fg3Made}/{fg3Att})</p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => recordStat(player.id, playerName, 'points_3pt')}
                className="bg-purple-500 hover:bg-purple-600 h-12 flex flex-col"
                data-testid={`stat-btn-${player.id}-points_3pt`}
              >
                <span className="text-lg font-bold">3PT ✓</span>
                <span className="text-xs opacity-75">{fg3Made} made</span>
              </Button>
              <Button
                onClick={() => recordStat(player.id, playerName, 'points_3pt_miss')}
                className="bg-purple-300 hover:bg-purple-400 h-12 flex flex-col text-purple-900"
                data-testid={`stat-btn-${player.id}-points_3pt_miss`}
              >
                <span className="text-lg font-bold">3PT ✗</span>
                <span className="text-xs opacity-75">{fg3Miss} miss</span>
              </Button>
            </div>
          </div>

          {/* Points Section - FT */}
          <div className="mb-3">
            <p className="text-xs font-semibold text-muted-foreground mb-2">FREE THROWS ({ftMade}/{ftAtt})</p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => recordStat(player.id, playerName, 'points_ft')}
                className="bg-green-500 hover:bg-green-600 h-12 flex flex-col"
                data-testid={`stat-btn-${player.id}-points_ft`}
              >
                <span className="text-lg font-bold">FT ✓</span>
                <span className="text-xs opacity-75">{ftMade} made</span>
              </Button>
              <Button
                onClick={() => recordStat(player.id, playerName, 'points_ft_miss')}
                className="bg-green-300 hover:bg-green-400 h-12 flex flex-col text-green-900"
                data-testid={`stat-btn-${player.id}-points_ft_miss`}
              >
                <span className="text-lg font-bold">FT ✗</span>
                <span className="text-xs opacity-75">{ftMiss} miss</span>
              </Button>
            </div>
          </div>
          
          {/* Rebounds Section */}
          <div className="mb-3">
            <p className="text-xs font-semibold text-muted-foreground mb-2">REBOUNDS ({stats?.total_rebounds || 0})</p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => recordStat(player.id, playerName, 'rebounds_off')}
                className="bg-orange-500 hover:bg-orange-600 h-10"
                data-testid={`stat-btn-${player.id}-rebounds_off`}
              >
                <span className="text-sm font-bold">OFF REB</span>
                <span className="ml-2 text-xs opacity-75">({stats?.rebounds_off || 0})</span>
              </Button>
              <Button
                onClick={() => recordStat(player.id, playerName, 'rebounds_def')}
                className="bg-yellow-500 hover:bg-yellow-600 h-10"
                data-testid={`stat-btn-${player.id}-rebounds_def`}
              >
                <span className="text-sm font-bold">DEF REB</span>
                <span className="ml-2 text-xs opacity-75">({stats?.rebounds_def || 0})</span>
              </Button>
            </div>
          </div>

          {/* Other Stats */}
          <div className="grid grid-cols-5 gap-1">
            <Button
              onClick={() => recordStat(player.id, playerName, 'assists')}
              variant="outline"
              className="h-10 text-xs p-1 border-2 hover:bg-cyan-500 hover:text-white"
              data-testid={`stat-btn-${player.id}-assists`}
            >
              <div className="flex flex-col items-center">
                <span className="font-bold">AST</span>
                <span className="text-[10px]">{stats?.assists || 0}</span>
              </div>
            </Button>
            <Button
              onClick={() => recordStat(player.id, playerName, 'steals')}
              variant="outline"
              className="h-10 text-xs p-1 border-2 hover:bg-emerald-500 hover:text-white"
              data-testid={`stat-btn-${player.id}-steals`}
            >
              <div className="flex flex-col items-center">
                <span className="font-bold">STL</span>
                <span className="text-[10px]">{stats?.steals || 0}</span>
              </div>
            </Button>
            <Button
              onClick={() => recordStat(player.id, playerName, 'turnovers')}
              variant="outline"
              className="h-10 text-xs p-1 border-2 hover:bg-red-500 hover:text-white"
              data-testid={`stat-btn-${player.id}-turnovers`}
            >
              <div className="flex flex-col items-center">
                <span className="font-bold">TO</span>
                <span className="text-[10px]">{stats?.turnovers || 0}</span>
              </div>
            </Button>
            <Button
              onClick={() => recordStat(player.id, playerName, 'fouls')}
              variant="outline"
              className="h-10 text-xs p-1 border-2 hover:bg-rose-500 hover:text-white"
              data-testid={`stat-btn-${player.id}-fouls`}
            >
              <div className="flex flex-col items-center">
                <span className="font-bold">FOUL</span>
                <span className="text-[10px]">{stats?.fouls || 0}</span>
              </div>
            </Button>
            <Button
              onClick={() => recordStat(player.id, playerName, 'technical_fouls')}
              variant="outline"
              className="h-10 text-xs p-1 border-2 hover:bg-rose-700 hover:text-white"
              data-testid={`stat-btn-${player.id}-technical-fouls`}
            >
              <div className="flex flex-col items-center">
                <span className="font-bold">TECH</span>
                <span className="text-[10px]">{stats?.technical_fouls || 0}</span>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Box Score Component
  const BoxScoreView = () => {
    if (!boxScoreData || !boxScoreSession) return null;

    return (
      <Dialog open={showBoxScore} onOpenChange={setShowBoxScore}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase flex items-center justify-between">
              <span>Box Score</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={printBoxScore} data-testid="print-box-score">
                  <Printer className="h-4 w-4 mr-1" /> Print
                </Button>
                <Button variant="outline" size="sm" onClick={downloadBoxScore} data-testid="download-box-score">
                  <Download className="h-4 w-4 mr-1" /> CSV
                </Button>
                <Button variant="outline" size="sm" onClick={shareBoxScore} data-testid="share-box-score">
                  <Share2 className="h-4 w-4 mr-1" /> Share
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div ref={boxScoreRef} className="box-score-content">
            {/* Header */}
            <div className="text-center border-b-2 border-primary pb-4 mb-4">
              <h1 className="text-2xl font-bold text-primary">{boxScoreSession.game_name}</h1>
              <p className="text-muted-foreground">
                {boxScoreSession.game_date}
                {boxScoreSession.opponent && ` • vs ${boxScoreSession.opponent}`}
              </p>
              <div className="flex items-center justify-center gap-6 mt-3" data-testid="box-score-final">
                <div className="text-center">
                  <p className="text-5xl font-black text-primary">{boxScoreData.final_score?.us ?? boxScoreData.team_totals?.total_points ?? 0}</p>
                  <p className="text-sm text-muted-foreground uppercase font-semibold">Us</p>
                </div>
                <div className="text-center">
                  {boxScoreData.final_score && (
                    <Badge className={`text-lg px-3 py-1 ${boxScoreData.final_score.result === 'W' ? 'bg-green-600' : boxScoreData.final_score.result === 'L' ? 'bg-red-600' : 'bg-gray-500'}`}>
                      {boxScoreData.final_score.result === 'W' ? 'WIN' : boxScoreData.final_score.result === 'L' ? 'LOSS' : 'TIE'}
                    </Badge>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">FINAL</p>
                </div>
                <div className="text-center">
                  <p className="text-5xl font-black text-red-600">{boxScoreData.final_score?.opponent ?? 0}</p>
                  <p className="text-sm text-muted-foreground uppercase font-semibold truncate max-w-[140px]">
                    {boxScoreSession.opponent || 'Opponent'}
                  </p>
                </div>
              </div>
            </div>

            {/* Score By Quarter (both teams) */}
            <div className="mb-4 overflow-x-auto" data-testid="box-score-quarters">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-primary text-primary-foreground">
                    <th className="border p-2 text-left">SCORE BY QUARTER</th>
                    <th className="border p-2 text-center">Q1</th>
                    <th className="border p-2 text-center">Q2</th>
                    <th className="border p-2 text-center">Q3</th>
                    <th className="border p-2 text-center">Q4</th>
                    <th className="border p-2 text-center">OT</th>
                    <th className="border p-2 text-center">FINAL</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-muted/30">
                    <td className="border p-2 font-bold">US</td>
                    {['q1', 'q2', 'q3', 'q4', 'ot'].map((q) => (
                      <td key={q} className="border p-2 text-center">{boxScoreData.quarter_totals?.[q] || 0}</td>
                    ))}
                    <td className="border p-2 text-center font-black text-primary">
                      {boxScoreData.final_score?.us ?? boxScoreData.team_totals?.total_points ?? 0}
                    </td>
                  </tr>
                  <tr>
                    <td className="border p-2 font-bold">{(boxScoreSession.opponent || 'OPPONENT').toUpperCase()}</td>
                    {['q1', 'q2', 'q3', 'q4', 'ot'].map((q) => (
                      <td key={q} className="border p-2 text-center">{boxScoreData.opponent_quarter_scores?.[q] || 0}</td>
                    ))}
                    <td className="border p-2 text-center font-black text-red-600">
                      {boxScoreData.final_score?.opponent ?? 0}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Stats Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-primary text-primary-foreground">
                    <th className="border p-2 text-left">Player</th>
                    <th className="border p-2 text-center">PTS</th>
                    <th className="border p-2 text-center">2PT</th>
                    <th className="border p-2 text-center">2P%</th>
                    <th className="border p-2 text-center">3PT</th>
                    <th className="border p-2 text-center">3P%</th>
                    <th className="border p-2 text-center">FT</th>
                    <th className="border p-2 text-center">FT%</th>
                    <th className="border p-2 text-center">REB</th>
                    <th className="border p-2 text-center">AST</th>
                    <th className="border p-2 text-center">STL</th>
                    <th className="border p-2 text-center">TO</th>
                    <th className="border p-2 text-center">PF</th>
                  </tr>
                </thead>
                <tbody>
                  {boxScoreData.player_stats?.map((player, index) => {
                    const fg2Att = (player.points_2pt || 0) + (player.points_2pt_miss || 0);
                    const fg3Att = (player.points_3pt || 0) + (player.points_3pt_miss || 0);
                    const ftAtt = (player.points_ft || 0) + (player.points_ft_miss || 0);
                    const fg2Pct = fg2Att > 0 ? ((player.points_2pt / fg2Att) * 100).toFixed(0) : '-';
                    const fg3Pct = fg3Att > 0 ? ((player.points_3pt / fg3Att) * 100).toFixed(0) : '-';
                    const ftPct = ftAtt > 0 ? ((player.points_ft / ftAtt) * 100).toFixed(0) : '-';
                    return (
                      <tr key={player.player_id} className={index % 2 === 0 ? 'bg-muted/30' : ''}>
                        <td className="border p-2 font-medium">{player.player_name}</td>
                        <td className="border p-2 text-center font-bold">{player.total_points}</td>
                        <td className="border p-2 text-center">{player.points_2pt}-{fg2Att}</td>
                        <td className="border p-2 text-center text-blue-600">{fg2Pct}%</td>
                        <td className="border p-2 text-center">{player.points_3pt}-{fg3Att}</td>
                        <td className="border p-2 text-center text-purple-600">{fg3Pct}%</td>
                        <td className="border p-2 text-center">{player.points_ft}-{ftAtt}</td>
                        <td className="border p-2 text-center text-green-600">{ftPct}%</td>
                        <td className="border p-2 text-center font-semibold">{player.total_rebounds}</td>
                        <td className="border p-2 text-center">{player.assists}</td>
                        <td className="border p-2 text-center">{player.steals}</td>
                        <td className="border p-2 text-center">{player.turnovers}</td>
                        <td className="border p-2 text-center">{player.fouls}</td>
                      </tr>
                    );
                  })}
                  {/* Totals Row */}
                  <tr className="bg-primary/10 font-bold">
                    <td className="border p-2">TEAM TOTALS</td>
                    <td className="border p-2 text-center">{boxScoreData.team_totals?.total_points || 0}</td>
                    <td className="border p-2 text-center">-</td>
                    <td className="border p-2 text-center">-</td>
                    <td className="border p-2 text-center">-</td>
                    <td className="border p-2 text-center">-</td>
                    <td className="border p-2 text-center">-</td>
                    <td className="border p-2 text-center">-</td>
                    <td className="border p-2 text-center">{boxScoreData.team_totals?.total_rebounds || 0}</td>
                    <td className="border p-2 text-center">{boxScoreData.team_totals?.total_assists || 0}</td>
                    <td className="border p-2 text-center">{boxScoreData.team_totals?.total_steals || 0}</td>
                    <td className="border p-2 text-center">{boxScoreData.team_totals?.total_turnovers || 0}</td>
                    <td className="border p-2 text-center">{boxScoreData.team_totals?.total_fouls || 0}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Team Summary Cards */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-4">
              <Card className="text-center p-2 bg-blue-50">
                <p className="text-xl font-bold text-blue-700">{boxScoreData.team_totals?.total_points || 0}</p>
                <p className="text-xs text-blue-600">POINTS</p>
              </Card>
              <Card className="text-center p-2 bg-orange-50">
                <p className="text-xl font-bold text-orange-700">{boxScoreData.team_totals?.total_rebounds || 0}</p>
                <p className="text-xs text-orange-600">REBOUNDS</p>
              </Card>
              <Card className="text-center p-2 bg-cyan-50">
                <p className="text-xl font-bold text-cyan-700">{boxScoreData.team_totals?.total_assists || 0}</p>
                <p className="text-xs text-cyan-600">ASSISTS</p>
              </Card>
              <Card className="text-center p-2 bg-emerald-50">
                <p className="text-xl font-bold text-emerald-700">{boxScoreData.team_totals?.total_steals || 0}</p>
                <p className="text-xs text-emerald-600">STEALS</p>
              </Card>
              <Card className="text-center p-2 bg-red-50">
                <p className="text-xl font-bold text-red-700">{boxScoreData.team_totals?.total_turnovers || 0}</p>
                <p className="text-xs text-red-600">TURNOVERS</p>
              </Card>
              <Card className="text-center p-2 bg-rose-50">
                <p className="text-xl font-bold text-rose-700">{boxScoreData.team_totals?.total_fouls || 0}</p>
                <p className="text-xs text-rose-600">FOULS</p>
              </Card>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8 space-y-6" data-testid="stats-page">
        {!canView('stats') ? (
          <NoAccessScreen area="Stats" />
        ) : (
        <>
        {isReadOnly('stats') && <ReadOnlyBanner area="Stats" />}
        {endedSession && showRecapToast && (
          <div
            className={`rounded-md border-2 px-4 py-3 flex items-center justify-between gap-3 ${
              endedSession.recap_status === 'ready' ? 'border-green-400 bg-green-50 text-green-900' :
              endedSession.recap_status === 'failed' ? 'border-amber-400 bg-amber-50 text-amber-900' :
              'border-blue-400 bg-blue-50 text-blue-900'
            }`}
            data-testid="recap-status-banner"
          >
            <div className="flex items-center gap-2">
              {endedSession.recap_status === 'generating' && (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="font-semibold">Recap draft generating…</span>
                  <span className="text-sm">We'll ping you when it's ready.</span>
                </>
              )}
              {endedSession.recap_status === 'ready' && (
                <>
                  <Sparkles className="h-4 w-4" />
                  <span className="font-semibold">Recap draft ready.</span>
                  <span className="text-sm">Open the Social Media page to post it.</span>
                </>
              )}
              {endedSession.recap_status === 'failed' && (
                <>
                  <AlertTriangle className="h-4 w-4" />
                  <span className="font-semibold">Recap draft failed.</span>
                  <span className="text-sm">You can still create one manually from Reports.</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              {endedSession.recap_status === 'ready' && (
                <Button size="sm" variant="outline" onClick={() => window.location.href = '/social-media'} data-testid="view-recap-draft-btn">
                  View Draft
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setShowRecapToast(false)} data-testid="dismiss-recap-banner">
                Dismiss
              </Button>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between border-b border-border pb-6">
          <div>
            <h1 className="font-heading text-5xl font-black uppercase tracking-tighter text-primary">
              Game Statistics
            </h1>
            <p className="text-muted-foreground mt-2">Track live game stats with touch counters</p>
          </div>
          <div className="flex gap-2">
            {activeSession && (
              <Button variant="outline" onClick={() => loadBoxScoreData(activeSession)} data-testid="view-box-score-btn">
                <FileText className="h-4 w-4 mr-2" />
                BOX SCORE
              </Button>
            )}
            {canEdit('stats') && (activeSession ? (
              <Button variant="destructive" onClick={endGame} data-testid="end-game-btn">
                <Square className="h-4 w-4 mr-2" />
                END GAME
              </Button>
            ) : (
              <Dialog open={showNewGameDialog} onOpenChange={setShowNewGameDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="start-game-btn">
                    <Play className="h-4 w-4 mr-2" />
                    START GAME
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl uppercase">Start Game Day Stats</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Game Name</Label>
                      <Input
                        value={newGame.game_name}
                        onChange={(e) => setNewGame({ ...newGame, game_name: e.target.value })}
                        placeholder="e.g., vs Eagles - Season Opener"
                        data-testid="game-name-input"
                      />
                    </div>
                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={newGame.game_date}
                        onChange={(e) => setNewGame({ ...newGame, game_date: e.target.value })}
                        data-testid="game-date-input"
                      />
                    </div>
                    <div>
                      <Label>Opponent (Optional)</Label>
                      <Input
                        value={newGame.opponent}
                        onChange={(e) => setNewGame({ ...newGame, opponent: e.target.value })}
                        placeholder="e.g., Eagles"
                        data-testid="opponent-input"
                      />
                    </div>
                    <Button onClick={startNewGame} className="w-full" data-testid="confirm-start-btn">
                      <Play className="h-4 w-4 mr-2" />
                      START TRACKING
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            ))}
          </div>
        </div>

        <Tabs defaultValue="live" className="space-y-6">
          <TabsList className="bg-muted">
            <TabsTrigger value="live" data-testid="live-tab">
              <Target className="h-4 w-4 mr-2" />
              Live Tracking
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="history-tab">
              <Trophy className="h-4 w-4 mr-2" />
              Game History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="live" className="space-y-6">
            {activeSession ? (
              <>
                {/* Active Game Header */}
                <Card className="border-2 border-primary bg-primary/5">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Badge className="bg-green-500 animate-pulse mb-2">LIVE</Badge>
                        <h2 className="font-heading text-2xl uppercase">{activeSession.game_name}</h2>
                        <p className="text-muted-foreground">
                          {activeSession.opponent && `vs ${activeSession.opponent} • `}
                          {activeSession.game_date}
                        </p>
                      </div>
                      <div className="flex items-center gap-5">
                        <div className="text-center">
                          <p className="text-4xl font-bold text-primary" data-testid="live-our-score">
                            {sessionStats.team_totals?.total_points || 0}
                          </p>
                          <p className="text-xs text-muted-foreground uppercase font-semibold">Us</p>
                        </div>
                        <span className="text-2xl font-bold text-muted-foreground">–</span>
                        <div className="text-center">
                          <p className="text-4xl font-bold text-red-600" data-testid="live-opp-score">
                            {Object.values(oppScores).reduce((a, b) => a + (b || 0), 0)}
                          </p>
                          <p className="text-xs text-muted-foreground uppercase font-semibold truncate max-w-[100px]">
                            {activeSession.opponent || 'Opponent'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Opponent Score Per Quarter */}
                    <div className="mt-4 pt-4 border-t flex flex-wrap items-end gap-3" data-testid="opponent-score-box">
                      <span className="text-sm font-semibold text-muted-foreground mb-2 mr-1">
                        {(activeSession.opponent || 'OPPONENT').toUpperCase()} SCORE:
                      </span>
                      {['q1', 'q2', 'q3', 'q4', 'ot'].map((q) => (
                        <div key={q} className="flex flex-col items-center">
                          <Label className="text-[10px] uppercase text-muted-foreground mb-1">{q}</Label>
                          <Input
                            type="number"
                            min="0"
                            value={oppScores[q] || ''}
                            placeholder="0"
                            onChange={(e) => updateOppScore(q, e.target.value)}
                            className="w-16 h-9 text-center font-bold"
                            data-testid={`opp-score-${q}`}
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Quarter Selector */}
                <Card className="border-2">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-center gap-4">
                      <span className="text-sm font-semibold text-muted-foreground">QUARTER:</span>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4].map((q) => (
                          <Button
                            key={q}
                            variant={currentQuarter === q ? "default" : "outline"}
                            size="lg"
                            onClick={() => setCurrentQuarter(q)}
                            className={`w-14 h-14 text-xl font-bold ${currentQuarter === q ? 'bg-primary' : ''}`}
                            data-testid={`quarter-${q}-btn`}
                          >
                            Q{q}
                          </Button>
                        ))}
                        <Button
                          variant={currentQuarter === 5 ? "default" : "outline"}
                          size="lg"
                          onClick={() => setCurrentQuarter(5)}
                          className={`w-14 h-14 text-lg font-bold ${currentQuarter === 5 ? 'bg-primary' : ''}`}
                          data-testid="quarter-ot-btn"
                        >
                          OT
                        </Button>
                      </div>
                    </div>

                    {/* Timeout Tracker (Our Team Only) */}
                    <div className="mt-4 pt-4 border-t flex flex-wrap items-center justify-center gap-4" data-testid="timeout-tracker">
                      <span className="text-sm font-semibold text-muted-foreground">OUR TIMEOUTS:</span>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          onClick={() => takeTimeout('full')}
                          disabled={(sessionStats.timeouts_used?.full || 0) >= (sessionStats.timeout_limits?.full || 3)}
                          data-testid="use-full-timeout-btn"
                        >
                          Full Timeout ({sessionStats.timeouts_used?.full || 0}/{sessionStats.timeout_limits?.full || 3})
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => undoTimeout('full')}
                          disabled={(sessionStats.timeouts_used?.full || 0) === 0}
                          title="Undo last full timeout"
                          data-testid="undo-full-timeout-btn"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          onClick={() => takeTimeout('short')}
                          disabled={(sessionStats.timeouts_used?.short || 0) >= (sessionStats.timeout_limits?.short || 2)}
                          data-testid="use-short-timeout-btn"
                        >
                          30sec Timeout ({sessionStats.timeouts_used?.short || 0}/{sessionStats.timeout_limits?.short || 2})
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => undoTimeout('short')}
                          disabled={(sessionStats.timeouts_used?.short || 0) === 0}
                          title="Undo last 30sec timeout"
                          data-testid="undo-short-timeout-btn"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Team Summary */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  <Card className="text-center p-3">
                    <p className="text-2xl font-bold">{sessionStats.team_totals?.total_points || 0}</p>
                    <p className="text-xs text-muted-foreground">POINTS</p>
                  </Card>
                  <Card className="text-center p-3">
                    <p className="text-2xl font-bold">{sessionStats.team_totals?.total_rebounds || 0}</p>
                    <p className="text-xs text-muted-foreground">REBOUNDS</p>
                  </Card>
                  <Card className="text-center p-3">
                    <p className="text-2xl font-bold">{sessionStats.team_totals?.total_assists || 0}</p>
                    <p className="text-xs text-muted-foreground">ASSISTS</p>
                  </Card>
                  <Card className="text-center p-3">
                    <p className="text-2xl font-bold">{sessionStats.team_totals?.total_steals || 0}</p>
                    <p className="text-xs text-muted-foreground">STEALS</p>
                  </Card>
                  <Card className="text-center p-3">
                    <p className="text-2xl font-bold">{sessionStats.team_totals?.total_turnovers || 0}</p>
                    <p className="text-xs text-muted-foreground">TURNOVERS</p>
                  </Card>
                  <Card className="text-center p-3">
                    <p className="text-2xl font-bold">{sessionStats.team_totals?.total_fouls || 0}</p>
                    <p className="text-xs text-muted-foreground">FOULS</p>
                  </Card>
                </div>

                {/* Recent Actions & Undo */}
                {recentStats.length > 0 && (
                  <Card className="border-2">
                    <CardHeader className="py-2 px-4">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <RotateCcw className="h-4 w-4" />
                        Recent (Tap to Undo)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-2">
                      <div className="flex flex-wrap gap-2">
                        {recentStats.map(stat => {
                          const statInfo = STAT_TYPES.find(s => s.key === stat.stat_type);
                          return (
                            <Badge
                              key={stat.id}
                              variant="outline"
                              className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                              onClick={() => undoStat(stat.id)}
                            >
                              {stat.player_name}: {statInfo?.label || stat.stat_type}
                              <Minus className="h-3 w-3 ml-1" />
                            </Badge>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Player Stat Counters - most recently active player floats to the top */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="player-stat-grid">
                  {orderedPlayers.map(player => (
                    <StatCounter key={player.id} player={player} />
                  ))}
                </div>

                {players.length === 0 && (
                  <Card className="border-2 border-dashed">
                    <CardContent className="p-12 text-center">
                      <Users className="h-12 w-12 mx-auto text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground mt-4">No players found</p>
                      <p className="text-sm text-muted-foreground">Add players to your roster first</p>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="border-2 border-dashed">
                <CardContent className="p-12 text-center">
                  <Target className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
                  <h3 className="font-heading text-2xl mt-4">No Active Game</h3>
                  <p className="text-muted-foreground mt-2">Start a game session to track live stats</p>
                  <Button className="mt-4" onClick={() => setShowNewGameDialog(true)}>
                    <Play className="h-4 w-4 mr-2" />
                    START GAME
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="history" className="space-y-4">
            {sessions.length === 0 ? (
              <Card className="border-2 border-dashed">
                <CardContent className="p-12 text-center">
                  <Trophy className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
                  <h3 className="font-heading text-2xl mt-4">No Game History</h3>
                  <p className="text-muted-foreground mt-2">Complete games will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {sessions.map(session => (
                  <Card key={session.id} className="border-2" data-testid={`game-session-${session.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold">{session.game_name}</h3>
                            <Badge variant={session.status === 'active' ? 'default' : 'secondary'}>
                              {session.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {session.opponent && `vs ${session.opponent} • `}
                            {session.game_date}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => loadBoxScoreData(session)}
                            data-testid={`view-box-score-${session.id}`}
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Box Score
                          </Button>
                          {session.status === 'active' && (
                            <Button
                              size="sm"
                              onClick={() => setActiveSession(session)}
                            >
                              Continue
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
        </>
        )}
      </div>

      {/* Box Score Modal */}
      <BoxScoreView />
    </Layout>
  );
}
